import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { candidaturasDaEmpresa, registrarRetornoExterno } from "../services/api";

function dia(iso) { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; } }

/**
 * Todas as pessoas que se candidataram às vagas da empresa, num lugar só.
 *
 * Antes isto só existia dentro de cada vaga: para saber quem estava esperando resposta, era preciso abrir vaga
 * por vaga e expandir cada uma. Com 19 vagas, quem tinha sido encerrado sem retorno estava espalhado por seis
 * telas — e o que ninguém vê, ninguém resolve.
 *
 * A ordem é por QUEM PRECISA DE AÇÃO, não por data nem por match: primeiro quem já foi encerrado no sistema da
 * empresa e ainda não soube. Essa é a dívida que o eaiRH existe para cobrar, e ela tem que ser a primeira coisa
 * na tela de quem pode pagá-la.
 */
export default function CandidaturasEmpresa() {
  const [dados, setDados] = useState(null);
  const [filtro, setFiltro] = useState("acao");
  const [marcando, setMarcando] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => { candidaturasDaEmpresa().then(setDados).catch((e) => setErro(e.message)); }, []);

  async function marcarRespondido(c, canal) {
    setMarcando(`${c.vagaId}-${c.usuarioId}`); setErro("");
    try {
      const r = await registrarRetornoExterno(c.vagaId, c.usuarioId, canal);
      setDados((d) => ({
        ...d,
        encerradosSemRetorno: Math.max(0, d.encerradosSemRetorno - (c.etapaFinal ? 1 : 0)),
        respondidos: d.respondidos + 1,
        candidaturas: d.candidaturas.map((x) =>
          x.vagaId === c.vagaId && x.usuarioId === c.usuarioId
            ? { ...x, retornoEm: r.retornoEm, retornoForaDaPlataforma: true } : x),
      }));
    } catch (e) { setErro(e.message); } finally { setMarcando(null); }
  }

  if (erro && !dados) return <p role="alert" className="text-sm text-red-600">{erro}</p>;
  if (!dados) return <p className="text-gray-500">Carregando...</p>;

  const filtrada = dados.candidaturas.filter((c) =>
    filtro === "acao" ? c.etapaFinal && !c.retornoEm
    : filtro === "processo" ? !c.etapaFinal
    : filtro === "respondidos" ? !!c.retornoEm
    : true);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card v={dados.encerradosSemRetorno} l="esperando resposta" cor="text-amber-600" destaque={dados.encerradosSemRetorno > 0} />
        <Card v={dados.emProcesso} l="em processo" cor="text-blue-600" />
        <Card v={dados.respondidos} l="respondidos" cor="text-green-600" />
        <Card v={dados.total} l="no total" cor="text-gray-700" />
      </div>

      {/* O alerta vem ANTES da lista e diz o número na cara. Um contador discreto entre outros contadores é
          fácil de olhar sem ver — e aqui cada unidade é uma pessoa atualizando a caixa de entrada. */}
      {dados.encerradosSemRetorno > 0 && (
        <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm">
          <b>{dados.encerradosSemRetorno} pessoa(s) já estão fora do processo no seu sistema e ainda não souberam.</b>{" "}
          Se você já respondeu por e-mail ou telefone, marque abaixo — assim o eaiRH para de cobrar e o candidato
          vê que houve retorno.
        </div>
      )}

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {[["acao", "Esperando resposta"], ["processo", "Em processo"], ["respondidos", "Respondidos"], ["todos", "Todos"]].map(([k, t]) => (
          <button key={k} onClick={() => setFiltro(k)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold border ${filtro === k
              ? "bg-primary text-white border-primary"
              : "bg-white text-gray-600 border-gray-200 hover:text-gray-900"}`}>{t}</button>
        ))}
      </div>

      {erro && <p role="alert" className="mb-3 text-sm text-red-600">{erro}</p>}

      {filtrada.length === 0 && (
        <p className="text-gray-500 text-sm">
          {filtro === "acao" ? "Ninguém esperando resposta. É assim que tem que ser." : "Nada por aqui."}
        </p>
      )}

      <div className="space-y-2">
        {filtrada.map((c) => (
          <div key={`${c.vagaId}-${c.usuarioId}`}
            className={`rounded-xl border bg-white p-3 shadow-sm ${c.etapaFinal && !c.retornoEm ? "border-amber-300" : "border-gray-200"}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-900">{c.nome}</span>
              {/* Etapa CRUA: quem lê é quem conduz o processo, e é o nome que ela reconhece no sistema dela. */}
              {c.etapa && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  c.etapaFinal && !c.retornoEm ? "bg-amber-100 text-amber-900"
                  : c.etapaFinal ? "bg-gray-100 text-gray-600" : "bg-blue-50 text-blue-800"}`}>
                  {c.etapa}{c.etapaEm && <> · {dia(c.etapaEm)}</>}
                </span>
              )}
              {c.retornoEm && (
                <span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[11px] font-semibold">
                  ✓ respondido{c.retornoForaDaPlataforma ? " por fora" : ""} em {dia(c.retornoEm)}
                </span>
              )}
              {!c.retornoEm && c.diasParado >= 7 && (
                <span className="text-[11px] text-amber-800">parado há {c.diasParado} dias</span>
              )}
            </div>

            <div className="text-xs text-gray-500 mt-1">
              <Link to={`/vagas/${c.vagaId}`} className="text-primary hover:underline font-medium">{c.vaga}</Link>
              {" · "}candidatou-se em {dia(c.candidaturaEm)}
              {!c.vagaAtiva && <span className="text-gray-400"> · vaga fora do ar</span>}
            </div>

            {!c.retornoEm && (
              <div className="mt-2">
                <RespondiPorFora c={c} onConfirmar={marcarRespondido} ocupado={marcando === `${c.vagaId}-${c.usuarioId}`} />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * "JÁ RESPONDI POR FORA" — agora com o canal, e avisando que o candidato vai saber (20/09/2026).
 *
 * O botão anterior era um clique só, e o title dele dizia "Não envia nada ao candidato". Era o caminho
 * mais barato da tela: diante de 36 pendências, 36 cliques zeravam a lista sem que ninguém do outro lado
 * ficasse sabendo de nada. Agora a empresa diz POR ONDE respondeu, e essa declaração chega a quem estava
 * esperando — que é a única pessoa capaz de confirmar ou desmentir.
 *
 * O aviso na tela não é ameaça, é honestidade: quem respondeu de verdade clica sem pensar duas vezes.
 */
function RespondiPorFora({ c, onConfirmar, ocupado }) {
  const [aberto, setAberto] = useState(false);
  const [canal, setCanal] = useState("email");

  if (ocupado) return <span className="px-3 py-1.5 text-xs font-semibold text-gray-500">Registrando...</span>;

  if (!aberto) {
    return (
      <button type="button" onClick={() => setAberto(true)}
        title="Use quando você já respondeu a esta pessoa por e-mail, telefone ou pelo seu sistema."
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
        ✓ Já respondi por fora
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-gray-300 bg-gray-50 p-3">
      <label className="block text-xs font-semibold text-gray-700">
        Por onde você respondeu {c.nome?.split(" ")[0] || "esta pessoa"}?
        <select value={canal} onChange={(e) => setCanal(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1 text-xs font-normal">
          <option value="email">Por e-mail</option>
          <option value="telefone">Por telefone</option>
          <option value="whatsapp">Por WhatsApp</option>
          <option value="ats">Pelo meu sistema de recrutamento</option>
          <option value="linkedin">Pelo LinkedIn</option>
          <option value="presencial">Pessoalmente</option>
        </select>
      </label>
      <p className="mt-2 text-[11px] leading-snug text-gray-600">
        Vamos avisar a pessoa de que você registrou esse retorno. Se ela não tiver recebido, poderá nos dizer.
      </p>
      <div className="mt-2 flex gap-2">
        <button type="button" onClick={() => { setAberto(false); onConfirmar(c, canal); }}
          className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90">Confirmar</button>
        <button type="button" onClick={() => setAberto(false)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-white">Cancelar</button>
      </div>
    </div>
  );
}

function Card({ v, l, cor, destaque }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 text-center ${destaque ? "border-amber-400 ring-1 ring-amber-200" : "border-gray-200"}`}>
      <div className={`text-2xl font-bold ${cor}`}>{v}</div>
      <div className="text-xs text-gray-500">{l}</div>
    </div>
  );
}
